import { LoaderFunction } from 'react-router-dom';

import { fetchGraphQL } from 'src/services/contentful/client';
import { ContentfulSmCourse, ContentfulSmCourseResponse, GET_SM_COURSE } from 'src/graphql/queries/GetSmCourse';
import { ContentfulSmLesson, ContentfulSmLessonResponse, GET_SM_LESSON } from 'src/graphql/queries/GetSmLesson';
import { GetCourseQueryVariables } from 'src/graphql/generated/GetSmCourse';
import { GetSmLessonQueryVariables } from 'src/graphql/generated/GetSmLesson';
import { GetLearningPathQueryVariables } from 'src/graphql/generated/graphql';
import {
  ContentfulLearningPath,
  ContentfulLearningPathResponse,
  GET_LEARNING_PATH,
} from 'src/graphql/queries/GetLearningPath';

export const loader: LoaderFunction = async ({ params, request }): Promise<LessonLoaderProps> => {
  const lessonId = params.lessonId ?? '';
  const courseId = params.courseId ?? '';
  const pathId = params.pathId ?? '';

  const preview = request.url.includes('preview=true');

  let lesson: ContentfulSmLesson | undefined;
  let course: ContentfulSmCourse | undefined;
  let learningPath: ContentfulLearningPath | undefined;

  if (lessonId) {
    lesson = await getLessonData(lessonId, preview);

    if (!lesson) {
      throw new Error('Lesson was not found');
    }
  }

  if (courseId) {
    course = await getCourse(courseId, preview);

    if (!course) {
      throw new Error('Course was not found');
    }
  }

  if (pathId) {
    learningPath = await getLearningPath(pathId, preview);

    if (!learningPath) {
      throw new Error('Learning path was not found');
    }
  }

  return { lesson: lesson!, course, learningPath };
};

const getLessonData = async (lessonId: string, preview: boolean): Promise<ContentfulSmLesson | undefined> => {
  const response = await fetchGraphQL<ContentfulSmLessonResponse, GetSmLessonQueryVariables>(GET_SM_LESSON, {
    slug: lessonId,
    preview,
  });

  return response.smLessonCollection.items[0];
};

const getCourse = async (courseId: string, preview: boolean): Promise<ContentfulSmCourse | undefined> => {
  const response = await fetchGraphQL<ContentfulSmCourseResponse, GetCourseQueryVariables>(GET_SM_COURSE, {
    slug: courseId,
    preview,
  });

  return response.smCourseCollection.items[0];
};

const getLearningPath = async (pathId: string, preview: boolean): Promise<ContentfulLearningPath | undefined> => {
  const response = await fetchGraphQL<ContentfulLearningPathResponse, GetLearningPathQueryVariables>(
    GET_LEARNING_PATH,
    {
      slug: pathId,
      preview,
    },
  );

  return response.learningPathCollection.items[0];
};

export interface LessonLoaderProps {
  lesson: ContentfulSmLesson;
  course?: ContentfulSmCourse;
  learningPath?: ContentfulLearningPath;
}
